import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardSidebar';
import api from '../services/api';

const CheckIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>;
const CameraIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0010.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const ChartIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>;

// Profile fields for completion calculation
const PROFILE_FIELDS = ['firstName', 'email', 'mobile', 'Address', 'State', 'Education'];

const calcCompletion = (u) => {
  if (!u) return 0;
  const filled = PROFILE_FIELDS.filter(f => u[f]).length;
  return Math.round((filled / PROFILE_FIELDS.length) * 100);
};

// ── Profile Edit Modal ────────────────────────────────────────────────────
const ProfileModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    firstName: user?.firstName || user?.FirstName || '',
    lastName:  user?.lastName  || user?.LastName  || '',
    mobile:    user?.mobile    || user?.Mobile    || '',
    Address:   user?.Address   || '',
    State:     user?.State     || '',
    Education: user?.Education || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await api.put('/users/profile', form);
      if (res.data.success) {
        onSaved(res.data.user || form);  // pass server-returned user data
        onClose();
      } else {
        setError(res.data.message || 'Update failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-accent-gold transition-colors";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a0b2e] border border-white/20 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h3 className="text-xl font-bold text-white">Update Profile Details</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-2xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">First Name</label>
              <input value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Last Name</label>
              <input value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Email</label>
            <input value={user?.email || user?.Email || ''} disabled className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white/40 cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Mobile Number</label>
            <input type="tel" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
              className={inputClass} placeholder="10-digit mobile" maxLength={10} />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Address</label>
            <input value={form.Address} onChange={e => setForm({...form, Address: e.target.value})} className={inputClass} placeholder="Your address" />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">State</label>
            <input value={form.State} onChange={e => setForm({...form, State: e.target.value})} className={inputClass} placeholder="Your state" />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Education / Qualification</label>
            <input value={form.Education} onChange={e => setForm({...form, Education: e.target.value})} className={inputClass} placeholder="e.g. B.Sc. Physics" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-white/20 rounded-xl text-white hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-3 btn-primary rounded-xl text-center disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Action Card ───────────────────────────────────────────────────────────
const ActionCard = ({ title, desc, icon, color, link }) => (
  <Link to={link} className={`p-5 rounded-2xl border border-white/5 transition-all hover:scale-[1.02] flex items-center gap-4 ${color}`}>
    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl backdrop-blur-sm">{icon}</div>
    <div><h4 className="font-bold text-lg">{title}</h4><p className="text-xs text-gray-300">{desc}</p></div>
  </Link>
);

// ── Dashboard Page ────────────────────────────────────────────────────────
const DashboardPage = () => {
  const { user, updateUser } = useAuth();

  const [activeCourse, setActiveCourse]       = useState('');
  const [loading, setLoading]                 = useState(true);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const [courses, setCourses]           = useState([]);
  const [currentAffairs, setCurrentAffairs] = useState([]);
  const [stats, setStats] = useState({ testsTaken: 0, avgScore: '0%', completion: 0 });

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      setLoading(true);
      try {
        // Subscriptions / courses
        const subsRes = await api.get('/users/subscriptions');
        const subs    = subsRes.data.data || [];
        const courseMap = new Map();
        subs.forEach(s => {
          if (!courseMap.has(s.CourseId)) courseMap.set(s.CourseId, { id: s.CourseId, title: s.CourseName || s.PackTitle || 'Course' });
        });
        const fmtCourses = Array.from(courseMap.values());
        setCourses(fmtCourses);

        // Restore active course from sessionStorage or default to first
        const saved = sessionStorage.getItem('activeCourse');
        if (saved && fmtCourses.some(c => String(c.id) === saved)) {
          setActiveCourse(saved);
        } else if (fmtCourses.length > 0) {
          setActiveCourse(String(fmtCourses[0].id));
        }

        // Dashboard stats
        const statsRes = await api.get('/users/dashboard');
        if (statsRes.data.success) {
          const qs = statsRes.data.data.quizStats || {};
          setStats({
            testsTaken: qs.totalQuizzes || 0,
            avgScore:   qs.avgScore ? Math.round(qs.avgScore) + '%' : '0%',
            completion: calcCompletion(user),
          });
        }

        // Current affairs
        const newsRes = await api.get('/current-affairs/latest');
        if (newsRes.data.success) setCurrentAffairs(newsRes.data.data.slice(0, 5));
      } catch (e) {
        console.error('Dashboard fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [user]);

  const handleCourseChange = (e) => {
    const v = e.target.value;
    setActiveCourse(v);
    sessionStorage.setItem('activeCourse', v);
  };

  const handleProfileSaved = (updatedUser) => {
    // Update AuthContext + localStorage so changes persist across navigation
    updateUser(updatedUser);
    // Recalculate profile completion with merged data
    const merged = { ...user, ...updatedUser };
    setStats(prev => ({ ...prev, completion: calcCompletion(merged) }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-full">
          <div className="text-accent-gold text-xl animate-pulse">Loading your dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      {profileModalOpen && (
        <ProfileModal user={user} onClose={() => setProfileModalOpen(false)} onSaved={handleProfileSaved} />
      )}

      <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8">

        {/* Welcome + course switcher */}
        <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
          <div>
            <h1 className="text-3xl font-bold">Welcome back, {user?.firstName || 'Student'}! 👋</h1>
            <p className="text-text-muted">Let's continue your preparation.</p>
          </div>
          <div className="w-full md:w-auto bg-white/10 rounded-xl p-1 border border-white/10 flex items-center">
            <span className="pl-3 text-sm text-text-muted hidden md:block">Active Course:</span>
            <select value={activeCourse} onChange={handleCourseChange}
              className="bg-transparent border-none text-white focus:ring-0 cursor-pointer py-2 pl-2 pr-8 text-sm font-medium w-full md:w-64">
              {courses.length > 0 ? courses.map(c => (
                <option key={c.id} value={c.id} className="bg-[#2d1b4e] text-white">{c.title}</option>
              )) : (
                <option className="bg-[#2d1b4e] text-white">Free Access</option>
              )}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT — Profile + Stats */}
          <div className="space-y-6">
            {/* Profile card */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-purple-600 to-blue-600 opacity-20" />
              <div className="relative mt-4 mb-4">
                <div className="w-24 h-24 mx-auto rounded-full p-1 bg-gradient-to-tr from-accent-gold to-purple-500">
                  <img
                    src={user?.profilePic || 'https://besst.in/assets/images/user-icon.png'}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover border-4 border-[#1a0b2e]"
                  />
                </div>
              </div>
              <h2 className="text-xl font-bold">{user?.firstName} {user?.lastName}</h2>
              <p className="text-sm text-text-muted mb-4">{user?.email}</p>
              <div className="bg-black/20 rounded-xl p-3 mb-4">
                <div className="flex justify-between text-xs mb-1">
                  <span>Profile Completion</span>
                  <span className="text-accent-gold">{stats.completion}%</span>
                </div>
                <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                  <div className="bg-accent-gold h-full rounded-full transition-all" style={{ width: `${stats.completion}%` }} />
                </div>
              </div>
              <button onClick={() => setProfileModalOpen(true)} className="text-sm text-purple-300 hover:text-white hover:underline">
                Update Profile Details
              </button>
            </div>

            {/* Performance card */}
            <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold flex items-center gap-2"><ChartIcon /> Performance</h3>
                <Link to="/performance" className="text-xs text-accent-gold hover:underline">View Full</Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/20 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold">{stats.testsTaken}</div>
                  <div className="text-xs text-text-muted">Tests Taken</div>
                </div>
                <div className="bg-black/20 p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-400">{stats.avgScore}</div>
                  <div className="text-xs text-text-muted">Avg. Score</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Test Actions + CA */}
          <div className="lg:col-span-2 space-y-6">
            <section>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent-gold rounded-full" />
                Start Learning
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ActionCard title="Practice Tests"       desc="Chapter-wise practice questions" icon="📝" color="bg-blue-500/20 hover:bg-blue-500/30"   link="/test-zone?type=Practice" />
                <ActionCard title="Mock Tests"           desc="Full-length exam simulation"     icon="⏱️" color="bg-purple-500/20 hover:bg-purple-500/30" link="/test-zone?type=Mock" />
                <ActionCard title="Current Affairs Test" desc="Daily GK & awareness quizzes"    icon="🌍" color="bg-green-500/20 hover:bg-green-500/30"   link="/current-affairs-quiz" />
              </div>
              <div className="mt-4">
                <Link to="/performance" className="p-4 rounded-2xl border border-white/5 bg-yellow-500/10 hover:bg-yellow-500/20 flex items-center gap-4 transition-all hover:scale-[1.01]">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl backdrop-blur-sm">📊</div>
                  <div><h4 className="font-bold text-lg">Test Analytics</h4><p className="text-xs text-gray-300">Review your past attempts</p></div>
                </Link>
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Current Affairs */}
              <div className="md:col-span-2 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold">Latest Current Affairs</h3>
                  <Link to="/current-affairs" className="text-xs bg-white/10 px-3 py-1 rounded-full hover:bg-white/20">View All</Link>
                </div>
                <div className="space-y-3 overflow-y-auto max-h-[280px] pr-1">
                  {currentAffairs.length > 0 ? currentAffairs.map((news, idx) => (
                    <Link key={news.currentAffairsId || idx} to="/current-affairs"
                      className="flex gap-3 group border-b border-white/5 pb-3 last:border-0 hover:bg-white/5 rounded-lg px-2 py-1 transition-colors">
                      <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-accent-gold" />
                      <div className="min-w-0">
                        <h4 className="font-medium text-sm text-text-light group-hover:text-accent-gold transition-colors line-clamp-2">
                          {news.Title || news.currentAffairsHead}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-text-muted">
                            {news.DateEvent ? new Date(news.DateEvent).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : ''}
                          </span>
                          {news.category && <span className="text-xs text-text-muted bg-white/5 px-2 py-0.5 rounded">{news.category}</span>}
                        </div>
                      </div>
                    </Link>
                  )) : (
                    <p className="text-text-muted text-sm">No updates available.</p>
                  )}
                </div>
                <Link to="/current-affairs" className="block text-center text-sm text-purple-300 mt-4 hover:text-white pt-2 border-t border-white/10">
                  View All Updates →
                </Link>
              </div>

              {/* Premium CTA */}
              <div className="bg-gradient-to-b from-accent-gold/20 to-transparent rounded-2xl p-6 border border-accent-gold/30 flex flex-col justify-between">
                <div>
                  <h3 className="font-bold text-accent-gold mb-2">Premium Access</h3>
                  <p className="text-sm text-gray-300 mb-4">Unlock all features with our PRO plan.</p>
                  <ul className="text-xs space-y-2 mb-6 text-gray-400">
                    <li className="flex gap-2"><CheckIcon /> Unlimited Mock Tests</li>
                    <li className="flex gap-2"><CheckIcon /> Expert Counselling</li>
                    <li className="flex gap-2"><CheckIcon /> Live Classes</li>
                  </ul>
                </div>
                <Link to="/subscriptions" className="btn-primary text-center py-2 rounded-lg text-sm w-full">Upgrade Now</Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;